import { asicInvoiceOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { paymentRouteOf } from './rowActions';

/**
 * The smallest charge Stripe will take, in AUD.
 *
 * Below this the backend answers "The total fee must be greater than 0,5AUD"
 * and nothing is paid, so the button is disabled rather than allowed to fail —
 * which is what the legacy portal did with its `totalAmount <= 0.5` guard.
 */
export const MINIMUM_CHARGE = 0.5;

/**
 * Stripe's merchant fee, charged on top of the amount owing.
 *
 * Bank transfer is cheaper to process than a card, and the director sees both
 * rates before choosing — which is the point of showing the fee on each method
 * rather than folding it into one total.
 */
export const MERCHANT_FEE_RATE = {
  card: 0.02,
  bankTransfer: 0.015,
} as const;

/** How the director is paying. `savedCard` and `newCard` are charged the same
 *  rate; they stay distinct because they send different `paymentMethod` values
 *  and collect different fields. */
export type PaymentMethod = 'savedCard' | 'newCard' | 'bankTransfer';

const rateFor = (method: PaymentMethod): number =>
  method === 'bankTransfer' ? MERCHANT_FEE_RATE.bankTransfer : MERCHANT_FEE_RATE.card;

/**
 * Whether the agent absorbs the merchant fee on this row.
 *
 * The legacy portal answered this differently depending on which screen you
 * were on, and the difference is preserved deliberately: `PayFees` waived on
 * `agentToPayMerchantFee && Number(agentFee) > 0`, while `PayInvoice` waived on
 * `agentToPayMerchantFee && !hasAsicInvoice`. Unifying them would change what
 * some directors are charged, so each route keeps the rule it was billed under.
 *
 * Both reduce to the same intent — an agent only absorbs the fee on their own
 * fee, never on money collected for ASIC.
 */
export const agentAbsorbsFee = (item: OutstandingItem, agentPaysFee: boolean): boolean => {
  if (!agentPaysFee) return false;

  const route = paymentRouteOf(item);
  // PayFees.tsx: the agent's own leg has to be part of what is being settled.
  if (route === 'annualReview') return item.agentAmount > 0;
  // PayInvoice.tsx waived on `!hasAsicInvoice`. `paymentRouteOf` reaches
  // 'formInvoice' on any form carrying an invoice, ASIC-issued ones included,
  // so the issuer is checked here rather than inferred from the route.
  return route === 'formInvoice' && asicInvoiceOf(item) === undefined;
};

/**
 * The merchant fee on an amount, rounded to cents.
 *
 * Rounded here rather than at the formatter because this figure is added to the
 * total: rounding only on display would show a fee and a total that disagree by
 * a cent, which is exactly the sort of thing a director queries.
 */
export const processingFeeOn = (
  subtotal: number,
  method: PaymentMethod,
  isAbsorbed: boolean,
): number => (isAbsorbed ? 0 : Math.round(subtotal * rateFor(method) * 100) / 100);
